import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { X } from "lucide-react";
import { config } from "@/lib/config";

const CONSENT_KEY = "cookie_consent";
const GEO_CACHE_KEY = "cookie_geo_region";

type ConsentStatus = "granted" | "denied" | null;

// EU/EEA country codes + UK
const REGULATED_COUNTRIES = new Set([
  "AT","BE","BG","HR","CY","CZ","DK","EE","FI","FR","DE","GR","HU","IE",
  "IT","LV","LT","LU","MT","NL","PL","PT","RO","SK","SI","ES","SE",
  "IS","LI","NO","GB",
]);

function getConsent(): ConsentStatus {
  const val = localStorage.getItem(CONSENT_KEY);
  if (val === "granted" || val === "denied") return val;
  return null;
}

function loadGA() {
  if (document.getElementById("ga-script")) return;
  const script = document.createElement("script");
  script.id = "ga-script";
  script.async = true;
  script.src = "https://www.googletagmanager.com/gtag/js?id=G-LV7EZ93509";
  document.head.appendChild(script);
  window.dataLayer = window.dataLayer || [];
  window.gtag = function () {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer!.push(arguments);
  };
  window.gtag("js", new Date());
  window.gtag("config", "G-LV7EZ93509");
}

async function isRegulatedRegion(): Promise<boolean> {
  // Check sessionStorage cache first
  const cached = sessionStorage.getItem(GEO_CACHE_KEY);
  if (cached === "regulated") return true;
  if (cached === "unregulated") return false;

  try {
    const res = await fetch("https://ipapi.co/json/", { signal: AbortSignal.timeout(3000) });
    if (!res.ok) return true; // default to showing banner on failure
    const data = await res.json();
    const country: string = data.country_code || "";
    const region: string = data.region_code || "";

    // EU/EEA/UK countries OR California
    const regulated = REGULATED_COUNTRIES.has(country) || (country === "US" && region === "CA");
    sessionStorage.setItem(GEO_CACHE_KEY, regulated ? "regulated" : "unregulated");
    return regulated;
  } catch {
    // On error, default to showing the banner (safe fallback)
    return true;
  }
}

export function CookieConsent() {
  const [visible, setVisible] = useState(false);

  const enabled = config.enableCookieConsent;

  // Always load GA regardless of consent setting
  useEffect(() => {
    loadGA();
  }, []);

  useEffect(() => {
    if (!enabled) return;

    const consent = getConsent();
    if (consent === "granted") {
      return;
    }
    if (consent === "denied") return;

    isRegulatedRegion().then((regulated) => {
      if (regulated) {
        setVisible(true);
      } else {
        localStorage.setItem(CONSENT_KEY, "granted");
      }
    });
  }, [enabled]);

  const handleAccept = useCallback(() => {
    localStorage.setItem(CONSENT_KEY, "granted");
    loadGA();
    setVisible(false);
  }, []);

  const handleDecline = useCallback(() => {
    localStorage.setItem(CONSENT_KEY, "denied");
    setVisible(false);
  }, []);

  if (!enabled || !visible) return null;

  return (
    <div className="fixed bottom-0 inset-x-0 z-[100] p-4 sm:p-6 pointer-events-none">
      <div className="pointer-events-auto mx-auto max-w-lg rounded-xl border border-border bg-card p-5 shadow-lg">
        <div className="flex items-start justify-between gap-4">
          <p className="text-sm text-muted-foreground leading-relaxed">
            We use cookies and analytics to understand how you use our site and
            improve your experience.{" "}
            <a href="/privacy" className="underline text-foreground hover:text-primary transition-colors">
              Privacy Policy
            </a>
          </p>
          <button
            onClick={handleDecline}
            className="text-muted-foreground hover:text-foreground transition-colors flex-shrink-0 mt-0.5"
            aria-label="Dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="flex items-center gap-3 mt-4">
          <Button size="sm" onClick={handleAccept}>
            Accept
          </Button>
          <Button size="sm" variant="outline" onClick={handleDecline}>
            Decline
          </Button>
        </div>
      </div>
    </div>
  );
}
